import type { ApiClient } from '@argzombie/api-client';
import { createContext, useContext, useSyncExternalStore } from 'react';
import type { Session, Tokens } from './session';

/** API origin, inlined at build time from VITE_API_BASE_URL. */
export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5080';

export type ApiContextValue = { client: ApiClient; baseUrl: string; session: Session };

export const ApiContext = createContext<ApiContextValue | null>(null);

/** The typed API client from <ApiProvider>. All API calls go through @argzombie/api-client. */
export function useApi(): ApiContextValue {
  const value = useContext(ApiContext);
  if (!value) throw new Error('useApi must be used inside <ApiProvider>');
  return value;
}

/** The signed-in tokens, re-rendering on sign-in and sign-out. */
export function useSession(): Tokens | null {
  const { session } = useApi();
  return useSyncExternalStore(
    (cb) => session.subscribe(cb),
    () => session.current,
  );
}

/** A readable message from an openapi-fetch error body (ProblemDetails) or a thrown error. */
export function problemMessage(error: unknown): string {
  if (error instanceof TypeError) return 'Could not reach the API.';
  if (error && typeof error === 'object') {
    const p = error as { detail?: string; title?: string; message?: string };
    return p.detail ?? p.title ?? p.message ?? 'Something went wrong.';
  }
  return String(error);
}

/**
 * Like `unwrap`, but keeps the API's machine code: the ProblemDetails `detail` plus its `code` extension as one line
 * ("No such code. (unknown_code)"). Throws so TanStack Query reports it.
 */
export function ok<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined) return result.data;
  const p = (result.error ?? {}) as { detail?: string; title?: string; code?: string };
  const message = p.detail ?? p.title ?? `HTTP ${result.response.status}`;
  throw new Error(p.code && p.code !== message ? `${message} (${p.code})` : message);
}

/** A thrown error as one line ("Could not reach the API." when fetch itself failed). */
export const errorText = (e: unknown) =>
  e instanceof TypeError ? 'Could not reach the API.' : e instanceof Error ? e.message : String(e);

/** Unwraps an openapi-fetch result, throwing the problem so TanStack Query reports it. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined) return result.data;
  throw new Error(problemMessage(result.error ?? `HTTP ${result.response.status}`));
}
